/**
 * Where the two editors that need more than a field go to get it.
 *
 * A link row has to search another table and a file has to be uploaded, and
 * both are done differently depending on who is asking. Somebody signed in
 * reads the table and uploads under their own account; a visitor filling in a
 * public form has neither, and the backend gives them two endpoints of their
 * own, scoped to the form they were sent a link to.
 *
 * A React context rather than another prop threaded through the grid, the row
 * modal and the editor registry: the two components that need it are the only
 * two that would ever read it, and everything between them has no opinion.
 * `FieldContext` is the wrong home -- it is the lookups a *field operation*
 * needs, it is used by the pure behaviour layer, and neither of these is a
 * lookup.
 */
import { createContext, use, type ReactNode } from 'react'

import type { Field, FieldOf } from '@/api/types'
import { uploadUserFile, type UserFileValue } from '../../../files/requests'
import { searchRows } from '../../../row/requests'
import { cellText } from '../../read'
import type { LinkedRow } from './link-row'

export interface EditorSources {
  /** Rows of the related table that a link row field could point at. */
  linkCandidates: (field: Field, search: string) => Promise<LinkedRow[]>
  /** Puts a file where a row can point at it. */
  uploadFile: (file: File) => Promise<UserFileValue>
}

/** How many candidates to offer at once. */
const CANDIDATES = 20

/**
 * The signed-in sources: the table itself, and the account's own files.
 *
 * A candidate is named by the related table's primary field, which is the only
 * column a link row cell ever holds -- so the picker shows exactly what the
 * cell will show.
 */
const OWN: EditorSources = {
  linkCandidates: async (field, search) => {
    const link = field as FieldOf<'link_row'>
    const tableId = link.link_row_table_id
    if (tableId === null || tableId === undefined) return []

    const limit = link.link_row_limit_selection_view_id
    const rows = await searchRows(tableId, {
      search,
      size: CANDIDATES,
      ...(limit === null || limit === undefined ? {} : { viewId: limit }),
    })

    // Typed without its discriminator by the generator -- see
    // `LinkRowPrimaryField` -- and `type` is the property it is read for.
    const primary = (link.link_row_table_primary_field as Field | null) ?? null

    return rows.map((row) => ({
      id: row.id,
      value: primary === null ? '' : cellText(primary, row),
    }))
  },

  uploadFile: uploadUserFile,
}

const Sources = createContext<EditorSources | null>(null)

/** The sources in force, which are the account's own unless something says otherwise. */
export function useEditorSources(): EditorSources {
  return use(Sources) ?? OWN
}

export function EditorSourcesProvider({
  sources,
  children,
}: {
  sources: EditorSources
  children: ReactNode
}) {
  return <Sources value={sources}>{children}</Sources>
}
